import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { outputAnnotationInfoStoreToken } from '../../../outputs/annotation/annotation-shared/annotation-providers';
import { PointType } from '../../../team-apis/otterdam/answers/shape-typings';
import { ShapeFeedback } from '../../../team-apis/otterdam/feedback';
import { CVFeedbackPopoverService } from '../../feedback/services/cv-feedback-popover.service';
import { ShapeSelectionService } from '../../outputs/annotation/annotation-shared/utils/shape-selection.service';
import { SamplingWorkspaceStore } from '../../sampling-workspace/state/sampling-workspace-store';
import { AbstractFeedbackDialogService } from './abstract-feedback-dialog.service';
import { ShapeFeedbackCrudService } from './crud/shape-feedback-crud.service';

@Injectable()
export class ShapeFeedbackDialogService extends AbstractFeedbackDialogService<ShapeFeedback> {
  protected override feedbackCrudService = inject(ShapeFeedbackCrudService);
  private shapeSelectionService = inject(ShapeSelectionService);
  private shapesStore = inject(outputAnnotationInfoStoreToken)('shapes');
  private sampleStore = inject(SamplingWorkspaceStore);
  private feedbackPopoverService = inject(CVFeedbackPopoverService);

  override setUpSubscriptions() {
    const output = this.outputInfoStore.state().output;
    if (this.taskWorkspaceStore.state().feedbackType === 'shape') {
      super.setUpSubscriptions();
      this.subscribeTo(
        combineLatest([
          // clear selected frame range when selection changes
          this.shapeSelectionService
            .getSelectionForOutput$()
            .pipe(distinctUntilChanged()),
          this.fusionManager.getFrameNumberForOutput$(output.name),
        ]),
        ([, frameNumber]) => {
          this.setSelectedFrameRange(frameNumber, frameNumber);
        },
      );
    }
  }

  openAddShapeFeedbackDialog() {
    this.feedbackListService.clearSelection();
    this.openAddFeedbackDialog();
  }

  openAddMissingShapeFeedbackDialog(point: PointType) {
    this.feedbackListService.clearSelection();
    this.openAddFeedbackDialog(point);
  }

  setSelectedFrameRange(startFrame: number, endFrame: number) {
    this.taskWorkspaceStore('feedbackPopover')('selectedFrameRange').set([
      startFrame,
      endFrame,
    ]);
  }

  override isEditable() {
    return (
      this.taskWorkspaceStore.state().workspace === 'sampling' &&
      !this.sampleStore.hasSampleFinalized()
    );
  }

  override closeDialog(selectedFeedbackId?: string) {
    if (!this.isDialogOpen()) {
      return;
    }

    if (isDefined(selectedFeedbackId)) {
      this.reopenFeedbackListWithSelection(selectedFeedbackId);
    } else {
      this.clearFeedbackForAllOutputs();
      this.taskWorkspaceStore('feedbackPopover')('missingShapePoint').set(
        undefined,
      );
      this.feedbackPopoverService.closePopover();
    }
  }

  override closeView() {}

  override openDetailView() {}

  override openEditFeedbackDialog() {
    this.feedbackPopoverService.openDialog();
  }

  protected override modifyShapeSelection(feedback: ShapeFeedback) {
    const shapeId = feedback.shapeId;
    if (isUndefined(shapeId)) {
      return;
    }

    const shape = this.shapesStore.state()[shapeId];
    if (!shape) {
      return;
    }

    // if previously selected missing shape feedback, the shape selection
    // might still be the same shape, so we clear it first
    this.taskWorkspaceStore('shapeSelection').set([]);
    this.taskWorkspaceStore('scopedObjectId').set(undefined);
    this.taskWorkspaceStore('scopedObjectId').set(shape.parentId);
    this.selectionService.setSelection([], [shapeId]);
  }

  protected override getStartFrame(feedback: ShapeFeedback) {
    return feedback.startFrame;
  }

  protected override changeOutput(feedback: ShapeFeedback) {
    this.feedbackListService.switchOutput(feedback.outputId);
  }

  private openAddFeedbackDialog(missingShapePoint?: PointType) {
    this.feedbackListService.closeFeedbackList();
    if (missingShapePoint) {
      this.taskWorkspaceStore('feedbackPopover')('missingShapePoint').set(
        missingShapePoint,
      );
    }

    this.openEditFeedbackDialog();
    if (missingShapePoint) {
      this.feedbackCrudService.setFeedbackCoordinates(
        missingShapePoint,
        this.isDialogOpen(),
      );
    } else {
      this.clearFeedbackMarkers();
    }
  }
}
